// ==========================================================================
// lib/quickSignIn.ts — Face ID / fingerprint as the second step (FF-061, 10 Oct 2026)
//   James: "I need it to remember us … or have a passcode or some way to log
//   in quickly". Supabase can hold a passkey as an MFA factor (WebAuthn). On
//   an iPhone that's Face ID; on a laptop, Touch ID or Windows Hello. The
//   policy stays the same (every session must reach aal2); the code from the
//   authenticator app is the fallback and still works everywhere.
//   The passkey belongs to the website address (home.osullivan.app), so one
//   made on the live app doesn't work on localhost, and vice versa.
// ==========================================================================
import { supabase } from "./supabase";

export type Factor = { id: string; type: "totp" | "webauthn" | string; name: string | null; verified: boolean };

export const canUsePasskeys = () => typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials;

export async function listFactors(): Promise<Factor[]> {
  const { data } = await supabase.auth.mfa.listFactors();
  return (data?.all ?? []).map((f) => ({ id: f.id, type: f.factor_type, name: f.friendly_name ?? null, verified: f.status === "verified" }));
}

/** Remember the last email so the sign-in screen is one tap. */
export const rememberEmail = (e: string) => { try { localStorage.setItem("last_email", e); } catch { /* fine */ } };
export const lastEmail = () => { try { return localStorage.getItem("last_email") ?? ""; } catch { return ""; } };

function deviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone"; if (/iPad/.test(ua)) return "iPad"; if (/Mac/.test(ua)) return "Mac"; if (/Windows/.test(ua)) return "Windows PC"; if (/Android/.test(ua)) return "Android"; return "This device";
}

/** Adds Face ID (or Touch ID / Windows Hello) on this device. Needs a full (aal2) session. */
export async function addPasskey(): Promise<{ ok: true } | { ok: false; message: string }> {
  if (!canUsePasskeys()) return { ok: false, message: "This browser can't do Face ID or fingerprint sign-in." };
  try {
    // Clear a half-finished one from an earlier attempt.
    for (const f of await listFactors()) if (f.type === "webauthn" && !f.verified) await supabase.auth.mfa.unenroll({ factorId: f.id });
    const { error } = await supabase.auth.mfa.webauthn.register({ friendlyName: `${deviceName()} ${new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short" })}` });
    if (error) return { ok: false, message: plain(error.message) };
    return { ok: true };
  } catch (e) { return { ok: false, message: plain((e as Error).message) }; }
}

/** The second step with Face ID: finds a passkey factor and asks the device. */
export async function signInWithPasskey(factorId: string): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    const { error } = await supabase.auth.mfa.webauthn.authenticate({ factorId });
    if (error) return { ok: false, message: plain(error.message) };
    return { ok: true };
  } catch (e) { return { ok: false, message: plain((e as Error).message) }; }
}

export async function removeFactor(id: string) {
  const { error } = await supabase.auth.mfa.unenroll({ factorId: id });
  if (error) throw new Error(plain(error.message));
}

function plain(m: string) {
  if (/not allowed|NotAllowedError|cancel|abort/i.test(m)) return "Cancelled, or Face ID didn't go through. Try again.";
  if (/not enabled|unsupported|disabled|webauthn/i.test(m) && /enabled|support/i.test(m)) return "Face ID sign-in isn't switched on for this app yet (Supabase → Authentication → Multi-Factor → WebAuthn).";
  if (/aal2|assurance/i.test(m)) return "Sign in fully first (with your code), then add Face ID.";
  return m;
}
